import { useEffect, useRef, useState } from "react";
import { Plus, Pin, PinOff } from "lucide-react";
import { SB } from "@/config/sidebarSizing";
import { THEME } from "@/config/theme";
import SubjectTree from "./SubjectTree";
import type { ApiProject, ApiForge } from "@/services/projectService";

// Inline subject creation (mirrors the workspace + forge create rows): the "+"
// opens this editable "Untitled Subject" row at the top of the tree instead of a
// modal. Rendered only while creating, so it mounts fresh (default title, focused)
// on every open.
function SubjectCreateInput({
  onCreate,
  onCancel,
}: {
  onCreate: (title: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState("Untitled Subject");
  const ref = useRef<HTMLInputElement>(null);
  // Guards against a double-create when Enter commits and the ensuing unmount
  // fires the input's blur.
  const submittingRef = useRef(false);

  useEffect(() => {
    ref.current?.select();
  }, []);

  const commit = async () => {
    if (submittingRef.current) return;
    const t = title.trim();
    if (!t) {
      onCancel();
      return;
    }
    submittingRef.current = true;
    await onCreate(t); // parent creates the subject + closes the flow
  };

  return (
    <div className="px-2 py-1">
      <input
        ref={ref}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") onCancel();
        }}
        placeholder="e.g. Network Architecture"
        // Match the subject rows' title typography so the row you type reads like
        // the subjects it joins.
        className={`w-full ${SB.text.subject} ${SB.font.sidebar} font-semibold ${THEME.sidebar.input} rounded px-1.5 py-0.5`}
      />
    </div>
  );
}

interface Props {
  project: ApiProject | null;
  onRenameSubject: (subjectId: string, title: string) => void;
  onDeleteSubject: (subjectId: string) => void;
  onCreateForge: (subjectId: string, title: string) => Promise<ApiForge>;
  onRenameForge: (forgeId: string, title: string) => void;
  onDeleteForge: (forgeId: string) => void;
  showCreateFlow: boolean;
  onShowCreateFlow: () => void;
  onHideCreateFlow: () => void;
  onCreateFlow: (subjectName: string) => Promise<void>;
  // Called when a forge is tapped — lets the parent auto-hide the mobile menu.
  onForgeSelect?: () => void;
  // Mobile-only pin control: keep the menu open across forge selections.
  isMobile?: boolean;
  isTreeLocked?: boolean;
  onToggleLock?: () => void;
}

export default function WorkspaceView({
  project,
  onRenameSubject,
  onDeleteSubject,
  onCreateForge,
  onRenameForge,
  onDeleteForge,
  showCreateFlow,
  onShowCreateFlow,
  onHideCreateFlow,
  onCreateFlow,
  onForgeSelect,
  isMobile = false,
  isTreeLocked = false,
  onToggleLock,
}: Props) {
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center px-3 pt-3 pb-2.5 gap-2 border-b border-white/[0.06] shrink-0">
        <span className={`${THEME.sidebar.mapsLabel} truncate flex-1 select-text`}>
          {project?.name ?? ""}
        </span>
        {isMobile && (
          <button
            onClick={onToggleLock}
            title={isTreeLocked ? "Unpin menu" : "Keep menu open"}
            aria-label={isTreeLocked ? "Unpin menu" : "Keep menu open"}
            aria-pressed={isTreeLocked}
            className={`p-1 rounded-md transition-colors duration-150 shrink-0 ${
              isTreeLocked
                ? "text-indigo-300 bg-indigo-500/15 hover:bg-indigo-500/25"
                : "text-slate-400 hover:text-slate-100 hover:bg-white/5"
            }`}
          >
            {isTreeLocked ? <Pin size={SB.icon.headerAdd} /> : <PinOff size={SB.icon.headerAdd} />}
          </button>
        )}
        <button
          onClick={onShowCreateFlow}
          title="New subject"
          className="p-1 rounded-md text-slate-400 hover:text-slate-100 hover:bg-white/5 transition-colors duration-150 shrink-0"
        >
          <Plus size={SB.icon.headerAdd} />
        </button>
      </div>

      <div className={`flex-1 overflow-y-auto overflow-x-hidden ${SB.row.list} ${THEME.scrollbar.dark}`}>
        {showCreateFlow && (
          <SubjectCreateInput onCreate={onCreateFlow} onCancel={onHideCreateFlow} />
        )}
        {project?.subjects.map((subject) => (
          <SubjectTree
            key={subject.id}
            subject={subject}
            project={project}
            onRename={onRenameSubject}
            onDelete={onDeleteSubject}
            onCreateForge={onCreateForge}
            onRenameForge={onRenameForge}
            onDeleteForge={onDeleteForge}
            onForgeSelect={onForgeSelect}
          />
        ))}
        {project && project.subjects.length === 0 && !showCreateFlow && (
          <p className={`${SB.text.forge} text-canvas-muted px-3 py-3`}>
            No subjects yet — click + to create one.
          </p>
        )}
      </div>
    </div>
  );
}
